import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useContextSelector } from "use-context-selector";
import { useTheme } from "styled-components";
import { useMediaQuery } from "usehooks-ts";
import { ColumnDef } from "@tanstack/react-table";
import { FaClock } from "react-icons/fa";
import { MdContentCopy, MdDoDisturb, MdDone } from "react-icons/md";
import { RiTruckFill } from "react-icons/ri";

import {
  OrderDetailSale,
  OrderDetailSaleItem,
  ProductList,
  ShipmentsPromotion,
} from "../../../../../../@types/Orders";
import { PickingItemStatus } from "../../../../../../enums/Orders";

import { OrdersAccordion } from "../OrdersAccordion";
import { Input } from "../../../../../../components/New/Input";
import { Table } from "../../../../../../components/New/Table";
import { ConfirmationModal } from "../../../../../../components/ConfirmationModal";

import { OrdersContext } from "../../../../../../contexts/OrdersContext";
import { useTranslateStatus } from "../../hooks/useTranslateStatus";
import { useAuth } from "../../../../../../hooks/auth";

import { colors } from "../../../../../../styles/themes/default/foundations/colors";
import MoneyBack from "../../../../../../assets/icons/money-back.svg";
import * as S from "./styles";

import {
  formatDateWithTenantFormat,
  formatOrderPrice,
  formatPercentage,
} from "../../../../../../shared/Formatter";
import { DropshipQtyText } from "../OrderDetails.shared.styles";
import ErrorMessage from "../ErrorMessage";
import { formatQuantityWithRefund } from "../formatQuantityWithRefund";

const promotionsTotalAmount = (promotion: ShipmentsPromotion[]) => {
  return promotion?.reduce((accumulator, currentPromotion) => {
    return accumulator + currentPromotion.amount;
  }, 0);
};

const calculateSubTotal = (
  totalValue: number,
  shippingValue: number,
  taxValue: number,
) => {
  const total = totalValue ?? 0;
  const shipping = shippingValue ?? 0;
  const tax = taxValue ?? 0;

  return total - shipping - tax;
};

const calculateTaxPercentage = (
  taxPrice: number,
  quantity: number,
  unitPrice: number,
) => {
  if (!taxPrice) return "0.00%";
  return formatPercentage((taxPrice / quantity / unitPrice) * 100);
};

const calculateItemTotal = (
  unitPrice: number,
  quantity: number,
  taxPrice: number,
  promotions: ShipmentsPromotion[],
) => {
  const baseTotal = unitPrice * quantity + (taxPrice || 0);
  const discount = Number(promotionsTotalAmount(promotions)) || 0;
  return baseTotal - discount;
};

const createPriceColumns = (
  t: (key: string) => string,
  currency: string,
): ColumnDef<ProductList>[] => [
  {
    accessorFn: (row) => row.product?.sku || "",
    header: t("sku"),
    cell: (props) => <S.BillingText>{props.getValue()}</S.BillingText>,
  },
  {
    accessorKey: "",
    header: t("checkoutStatus"),
    size: 200,
    cell: () => (
      <S.StatusContainer variant={"Completed"}>
        {t("completed")}
      </S.StatusContainer>
    ),
  },
  {
    accessorFn: (row) => row.productSummary.unitPrice || "",
    header: t("unitPrice"),
    cell: (props) => (
      <S.BillingText>
        {formatOrderPrice(
          props?.row.original.productSummary?.unitPrice,
          currency,
        )}
      </S.BillingText>
    ),
  },
  {
    accessorFn: (row) => row?.productSummary?.taxPrice || "",
    header: t("tax"),
    size: 150,
    cell: (props) => (
      <S.BillingText>
        {formatOrderPrice(
          props?.row.original?.productSummary?.taxPrice || 0,
          currency,
        )}
        <span
          style={{
            color: colors["gray-700"],
            marginLeft: "0.3rem",
          }}
        >
          (
          {calculateTaxPercentage(
            props?.row.original?.productSummary?.taxPrice || 0,
            props?.row.original?.quantity || 1,
            props?.row.original?.productSummary?.unitPrice || 1,
          )}
          )
        </span>
      </S.BillingText>
    ),
  },
  {
    accessorKey: "quantity",
    header: t("quantity"),
    cell: (props) => <S.BillingText>{props.getValue()}</S.BillingText>,
  },
  {
    accessorFn: (row) => row.promotions || "",
    header: t("discount"),
    cell: (props) => (
      <S.BillingText style={{ color: colors["red-450"] }}>
        (
        {formatOrderPrice(
          promotionsTotalAmount(props.row.original.promotions) || 0,
          currency,
        )}
        )
      </S.BillingText>
    ),
  },
  {
    accessorKey: "promotions",
    header: t("itemTotal"),
    cell: (props) => (
      <S.BillingText>
        {formatOrderPrice(
          calculateItemTotal(
            props.row.original?.productSummary?.unitPrice,
            props.row.original?.quantity,
            props.row.original?.productSummary?.taxPrice || 0,
            props.row.original.promotions,
          ),
          currency,
        )}
      </S.BillingText>
    ),
  },
];

const createShipmentsColumns = (
  t: (key: string) => string,
  orderDetails: { items?: ProductList[]; sourceMarketplace?: string },
  translatePickingItemStatus: (status: PickingItemStatus) => string,
  cancelShipmentsItem: (id: string) => void,
  loading: { cancelItem: boolean },
  canRefundAndCancelByCountry: boolean,
): ColumnDef<OrderDetailSaleItem>[] => [
  {
    accessorFn: (row) =>
      row.product?.lightspeedProduct?.manufacturerSku || row.product?.sku || "",
    header: t("sku"),
    size: 130,
    cell: (props) => <S.BillingText>{props.getValue()}</S.BillingText>,
  },
  {
    accessorFn: (row) =>
      row?.product?.lightspeedProduct?.description || row?.product?.name,
    header: t("title"),
    size: 332,
    cell: (props) => <S.BillingText>{props.getValue()}</S.BillingText>,
  },
  {
    accessorFn: (row) => {
      const orderItem = orderDetails?.items?.find(
        (item: ProductList) => item.product?.sku === row.product?.sku,
      );
      const specializedStock = orderItem?.stockChecks?.find(
        (stock) => stock.source === "SPECIALIZED",
      );

      const color = () => {
        if (!specializedStock?.availableQuantity) {
          return colors["gray-850"];
        }

        if ((specializedStock?.availableQuantity ?? 0) < (row?.quantity ?? 0)) {
          return colors["red-450"];
        }
        return colors["gray-850"];
      };

      return {
        availableQuantity: specializedStock?.availableQuantity ?? "-",
        color: color(),
      };
    },
    header: t("sbcDropshipQty"),
    size: 150,
    cell: (props) => {
      const { availableQuantity, color } = props.getValue() as {
        availableQuantity: string;
        color: string;
      };

      return (
        <DropshipQtyText color={color}>{availableQuantity}</DropshipQtyText>
      );
    },
  },
  {
    accessorFn: (row) => {
      const orderItem = orderDetails?.items?.find(
        (item: ProductList) => item.product?.sku === row.product?.sku,
      );
      const lightspeedStock = orderItem?.stockChecks?.find(
        (stock) => stock.source === "LIGHTSPEED",
      );

      const color = () => {
        if (!lightspeedStock?.availableQuantity) {
          return colors["gray-850"];
        }

        if ((lightspeedStock?.availableQuantity ?? 0) < (row?.quantity ?? 0)) {
          return colors["red-450"];
        }
        return colors["gray-850"];
      };

      return {
        availableQuantity: lightspeedStock?.availableQuantity ?? "-",
        color: color(),
      };
    },
    header: t("lightspeedQty"),
    size: 150,
    cell: (props) => {
      const { availableQuantity, color } = props.getValue() as {
        availableQuantity: string;
        color: string;
      };

      return (
        <DropshipQtyText color={color}>{availableQuantity}</DropshipQtyText>
      );
    },
  },
  {
    accessorKey: "quantity",
    header: t("quantity"),
    size: 100,
    cell: (props) => (
      <S.BillingText>
        {formatQuantityWithRefund(props.row.original, {
          fullyRefunded: t("fullyRefunded"),
          refunded: t("refundedQuantityLabel"),
        })}
      </S.BillingText>
    ),
  },
  {
    accessorFn: (row) =>
      row?.product?.lightspeedProduct?.description || row?.product?.name,
    header: t("status"),
    size: 150,
    cell: (props) => (
      <S.StatusContainer variant={props.row.original.status}>
        {translatePickingItemStatus(props.row.original.status)}
      </S.StatusContainer>
    ),
  },
  {
    accessorKey: "",
    header: t("actions"),
    size: 70,
    cell: (props) => (
      <S.ActionsContainer>
        <ConfirmationModal
          title={t("confirmCancelOrderLine")}
          description={t("cancelOrderLineDescription")}
          variant="delete"
          confirmFunction={() =>
            cancelShipmentsItem(props.row.original.id)
          }
        >
          <S.ActionButton
            title={t("cancelItem")}
            disabled={
              props.row.original.status !== PickingItemStatus.PENDING ||
              loading.cancelItem ||
              orderDetails.sourceMarketplace !== "SBC" ||
              !canRefundAndCancelByCountry
            }
          >
            <MdDoDisturb size={20} />
          </S.ActionButton>
        </ConfirmationModal>
        <S.ActionButton title={t("refundItem")} disabled>
          <img
            src={MoneyBack}
            alt=""
            style={{ opacity: 0.5, width: 23, height: 23 }}
          />
        </S.ActionButton>
      </S.ActionsContainer>
    ),
  },
];

export function OrderDetailsComplete() {
  const { t } = useTranslation();

  const { translatePickingItemStatus } = useTranslateStatus();

  const [copyToClipboard, setCopyToClipboard] = useState({
    generalShippingData: false,
    shippingEmail: false,
  });

  const { loading, orderDetails, cancelShipmentsItem } = useContextSelector(
    OrdersContext,
    (context) => {
      return context;
    },
  );

  const { canRefundAndCancelByCountry, shopSelected } = useAuth();

  const { responsiveDesign } = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${responsiveDesign.sm})`);

  const {
    createdAt,
    requestedShippingCarrier,
    requestedShippingClass,
    mustDeliverBy,
    privateNotes,
    publicNotes,
    additionalCostOrDiscount,
    currency,
  } = orderDetails;

  const dateFormat = shopSelected?.tenant?.dateFormat;

  const priceColumns = createPriceColumns(t, currency);

  const shipmentsColumns = createShipmentsColumns(
    t,
    orderDetails,
    translatePickingItemStatus,
    cancelShipmentsItem,
    loading,
    canRefundAndCancelByCountry,
  );

  const handleCopyInfo = (
    copyType: keyof typeof copyToClipboard,
    info?: string,
  ) => {
    if (!info) return;

    navigator.clipboard.writeText(info);
    setCopyToClipboard((state) => ({ ...state, [copyType]: true }));

    setTimeout(() => {
      setCopyToClipboard((state) => ({ ...state, [copyType]: false }));
    }, 2000);
  };

  const renderSummarySection = () => (
    <OrdersAccordion title={t("summary")}>
      <S.GridContainer>
        <S.SumaryContentColumn>
          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("lineItems")}:</S.SumaryDataTitle>
            <S.SumaryData>{orderDetails?.sales?.length}</S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("buyer")}:</S.SumaryDataTitle>
            <S.SumaryData>
              {orderDetails?.billing?.lastName ??
                orderDetails?.shipping?.lastName}
              ,{" "}
              {orderDetails?.billing?.firstName ??
                orderDetails?.shipping?.firstName}
            </S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("created")}:</S.SumaryDataTitle>
            <S.SumaryData>
              {formatDateWithTenantFormat(createdAt, dateFormat, true)}
            </S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("imported")}:</S.SumaryDataTitle>
            <S.SumaryData>
              {formatDateWithTenantFormat(
                orderDetails?.importDateUTC,
                dateFormat,
                true,
              )}
            </S.SumaryData>
          </S.AlignDataContainer>
        </S.SumaryContentColumn>
        <S.SumaryContentColumn>
          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("channelAdvisorOrderId")}:</S.SumaryDataTitle>
            <S.SumaryData>{orderDetails?.storeOrderID}</S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("sellerOrderId")}:</S.SumaryDataTitle>
            <S.SumaryData>{orderDetails?.secondaryStoreOrderID}</S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("siteOrderId")}:</S.SumaryDataTitle>
            <S.SumaryData>{orderDetails?.storeOrderID}</S.SumaryData>
          </S.AlignDataContainer>
        </S.SumaryContentColumn>
        <S.SumaryContentColumn>
          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("reqShippingShopify")}:</S.SumaryDataTitle>
            <S.SumaryData>{requestedShippingCarrier}</S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("carrierClass")}:</S.SumaryDataTitle>
            <S.SumaryData>{requestedShippingClass}</S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("estimatedShippingDate")}</S.SumaryDataTitle>
            <S.SumaryData>
              <Input
                defaultValue={
                  formatDateWithTenantFormat(
                    orderDetails?.estimateShip,
                    dateFormat,
                  ) || ""
                }
                disabled
              />
            </S.SumaryData>
          </S.AlignDataContainer>

          <S.AlignDataContainer>
            <S.SumaryDataTitle>{t("mustDeliverBy")}</S.SumaryDataTitle>
            <S.SumaryData>
              <Input
                defaultValue={
                  formatDateWithTenantFormat(mustDeliverBy, dateFormat) || ""
                }
                disabled
              />
            </S.SumaryData>
          </S.AlignDataContainer>
        </S.SumaryContentColumn>
      </S.GridContainer>
    </OrdersAccordion>
  );

  const renderShipmentSection = (sale: OrderDetailSale, saleIndex: number) => (
    <S.ShipmentsContainer key={sale.id}>
      <S.HorizontalContainer>
        <S.ShipContainer
          variant={sale?.shipmentData?.trackingNumber ? "SHIPPED" : "PENDING"}
        >
          <RiTruckFill
            size={24}
            color={
              !sale?.shipmentData?.trackingNumber
                ? colors["red-500"]
                : colors["green-650"]
            }
          />
          {sale?.shipmentData?.trackingNumber ?? t("unshipped")}
          <span style={{ marginLeft: "0.1rem" }}>
            {sale?.shipmentData?.dispatchDate &&
              `(${formatDateWithTenantFormat(sale?.shipmentData?.dispatchDate, dateFormat, true)})`}
          </span>
        </S.ShipContainer>
      </S.HorizontalContainer>

      <S.ShipmentsGrid>
        <S.InputContainer>
          <Input
            title={t("trackingNumber")}
            defaultValue={sale?.shipmentData?.trackingNumber || ""}
            disabled
          />

          <Input
            title={t("shipmentDate")}
            defaultValue={
              formatDateWithTenantFormat(
                sale?.shipmentData?.dispatchDate,
                dateFormat,
              ) || ""
            }
            disabled
          />
        </S.InputContainer>
        <S.InputContainer>
          <Input
            title={t("shippingCarrierClass")}
            defaultValue={requestedShippingClass || ""}
            disabled
          />

          <Input
            title={t("distributionCenter")}
            defaultValue={sale?.storeName || ""}
            disabled
          />
        </S.InputContainer>
        <S.InputContainer>
          <Input title={t("returnTrackingNumber")} disabled />
        </S.InputContainer>
      </S.ShipmentsGrid>

      <S.Separator style={{ marginTop: "1.5rem", marginBottom: "1.5rem" }} />

      <S.ProductListsTitle>{t("productList")}</S.ProductListsTitle>
      <S.NumberOfProducts>
        {sale?.items?.length} {t("productsListed")}
      </S.NumberOfProducts>

      {sale?.items?.length && (
        <Table
          columns={shipmentsColumns}
          columnToFilter="task"
          data={sale.items}
          key={`${sale.id}-${sale.items.map((item) => item.status).join("-")}`}
          style={{ marginTop: "1.5rem" }}
        />
      )}

      <S.Separator style={{ marginTop: "1.5rem", marginBottom: "1.5rem" }} />

      <S.ActivityContainer>
        <S.NotesContainer>
          {isMobile ? (
            <S.ProductListsTitle>{t("internalNotes")}</S.ProductListsTitle>
          ) : (
            <S.SumaryData>{t("internalNotes")}</S.SumaryData>
          )}
          <S.ActivityTextArea cols={10} rows={3} disabled />
        </S.NotesContainer>
        <S.ActivityAlign>
          {!isMobile && <S.SumaryData>{t("activityLog")}</S.SumaryData>}

          <S.ActivityContent>
            {sale?.actionHistory?.map(({ createdAt, message }, index) => (
              <S.ActivityReport key={index}>
                <S.ActivityDate>
                  <FaClock size={20} color={colors["gray-850"]} />
                  {formatDateWithTenantFormat(createdAt, dateFormat, true)}
                </S.ActivityDate>

                <span>{message}</span>
              </S.ActivityReport>
            ))}
          </S.ActivityContent>
        </S.ActivityAlign>
      </S.ActivityContainer>

      {saleIndex < orderDetails?.sales?.length - 1 && (
        <S.Separator
          style={{
            marginTop: isMobile ? "1.5rem" : "2.5rem",
            marginBottom: isMobile ? "1.5rem" : "2.5rem",
          }}
        />
      )}
    </S.ShipmentsContainer>
  );

  const renderShippingAndBillingSection = () => (
    <OrdersAccordion title={t("shippingAndBilling")}>
      <S.BillingContainer>
        <S.BillingTextContainer>
          <S.BillingAlignContainer>
            <div>
              <S.BillingTitleContainer>
                <S.BillingTitle>{t("shippingAddress")}</S.BillingTitle>
              </S.BillingTitleContainer>
              <S.BillingText>
                {orderDetails?.shipping?.lastName ?? ""},{" "}
                {orderDetails?.shipping?.firstName ?? ""}
              </S.BillingText>
              <S.BillingText>
                {orderDetails?.shipping?.companyName}
              </S.BillingText>
              <S.BillingText>
                {orderDetails?.shipping?.addressLine1}{" "}
                {orderDetails?.shipping?.addressLine2}
              </S.BillingText>
              <S.BillingText>
                {orderDetails?.shipping?.city},{" "}
                {orderDetails?.shipping?.stateOrProvinceName} -
                {orderDetails?.shipping?.postalCode}
              </S.BillingText>
              <S.BillingText>{orderDetails?.shipping?.country}</S.BillingText>
            </div>
            <div>
              <S.BillingTitleContainer>
                <S.BillingTitle>{t("email")}</S.BillingTitle>
                {copyToClipboard.shippingEmail ? (
                  <MdDone title={t("copied")} size={16} />
                ) : (
                  <MdContentCopy
                    title={t("copy")}
                    size={16}
                    onClick={() =>
                      handleCopyInfo(
                        "shippingEmail",
                        orderDetails?.buyerEmailAddress
                          ? orderDetails?.buyerEmailAddress
                          : "",
                      )
                    }
                    style={{ cursor: "pointer" }}
                  />
                )}
              </S.BillingTitleContainer>
              <S.BillingText>{orderDetails?.buyerEmailAddress}</S.BillingText>

              <S.BillingTitle
                style={{
                  marginTop: isMobile ? "1rem" : "1.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                {t("phone")}
              </S.BillingTitle>
              <S.BillingText>
                {orderDetails?.shipping?.daytimePhone}
              </S.BillingText>
            </div>
          </S.BillingAlignContainer>

          <S.BillingTitle
            style={{
              marginTop: isMobile ? "1rem" : "2rem",
              marginBottom: "0.5rem",
            }}
          >
            {t("buyerInstructions")}
          </S.BillingTitle>
          <S.BillingText>{orderDetails?.specialInstructions}</S.BillingText>
        </S.BillingTextContainer>

        <div
          style={{
            flex: 1,
            marginLeft: isMobile ? "" : "3.5rem",
            marginTop: isMobile ? "1rem" : "",
          }}
        >
          <S.BillingAlignContainer>
            <div>
              <S.BillingTitle style={{ marginBottom: "0.5rem" }}>
                {t("billingAddress")}
              </S.BillingTitle>
              <S.BillingText>
                {orderDetails?.billing?.lastName ??
                  orderDetails?.shipping?.lastName}
                ,{" "}
                {orderDetails?.billing?.firstName ??
                  orderDetails?.shipping?.firstName}
              </S.BillingText>
              <S.BillingText>
                {orderDetails?.billing?.companyName}
              </S.BillingText>
              <S.BillingText>
                {orderDetails?.billing?.addressLine1}{" "}
                {orderDetails?.billing?.addressLine2}
              </S.BillingText>
              <S.BillingText>
                {orderDetails?.billing?.city}, {orderDetails?.billing?.country}{" "}
                - {orderDetails?.billing?.postalCode}
              </S.BillingText>
              <S.BillingText>{orderDetails?.billing?.country}</S.BillingText>
            </div>
            <div>
              <S.BillingTitle style={{ marginBottom: "0.5rem" }}>
                {t("buyerId")}
              </S.BillingTitle>
              <S.BillingText>{orderDetails?.buyerEmailAddress}</S.BillingText>

              <S.BillingTitle
                style={{
                  marginTop: isMobile ? "1rem" : "1.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                {t("phone")}
              </S.BillingTitle>
              <S.BillingText>
                {orderDetails?.billing?.daytimePhone}
              </S.BillingText>
            </div>
          </S.BillingAlignContainer>
        </div>
      </S.BillingContainer>
    </OrdersAccordion>
  );

  const renderPriceSection = () => (
    <OrdersAccordion title={t("priceAndPromotions")}>
      <S.PriceContainer>
        {orderDetails?.items && (
          <Table
            columns={priceColumns}
            columnToFilter="task"
            data={orderDetails?.items}
          />
        )}

        <S.PriceDetails>
          <S.PriceWrapper>
            <p>{t("subtotal")}:</p>
            <p>
              {formatOrderPrice(
                calculateSubTotal(
                  orderDetails?.orderSummary?.totalPrice,
                  orderDetails?.orderSummary?.totalShippingPrice,
                  orderDetails?.orderSummary?.totalTaxPrice,
                ),
                currency,
              )}
            </p>
          </S.PriceWrapper>
          <S.PriceWrapper>
            <p>{t("shipping")}:</p>
            <p>
              {formatOrderPrice(
                orderDetails?.orderSummary?.totalShippingPrice || 0,
                currency,
              )}
            </p>
          </S.PriceWrapper>

          <S.PriceWrapper>
            <p>{t("tax")}:</p>
            <p>
              {formatOrderPrice(
                orderDetails?.orderSummary?.totalTaxPrice || 0,
                currency,
              )}
              <span style={{ marginLeft: "0.3rem" }}>
                (
                {orderDetails?.orderSummary?.totalTaxPrice
                  ? formatPercentage(
                      (orderDetails?.orderSummary?.totalTaxPrice /
                        calculateSubTotal(
                          orderDetails?.orderSummary?.totalPrice,
                          orderDetails?.orderSummary?.totalShippingPrice,
                          orderDetails?.orderSummary?.totalTaxPrice,
                        )) *
                        100,
                    )
                  : "0.00%"}
                )
              </span>
            </p>
          </S.PriceWrapper>

          {!!additionalCostOrDiscount && additionalCostOrDiscount !== 0 && (
            <S.PriceWrapper variant="discount">
              <p>
                {additionalCostOrDiscount < 0 && t("discount")}
                {additionalCostOrDiscount > 0 && t("additionalCost")}
              </p>
              <p>{formatOrderPrice(additionalCostOrDiscount, currency)}</p>
            </S.PriceWrapper>
          )}
          <S.PriceWrapper variant="total">
            <p>{t("total")}:</p>
            <p>
              {formatOrderPrice(
                orderDetails?.orderSummary?.totalPrice,
                currency,
              )}
            </p>
          </S.PriceWrapper>
        </S.PriceDetails>
      </S.PriceContainer>
    </OrdersAccordion>
  );

  return (
    <>
      <ErrorMessage orderDetails={orderDetails} />

      {renderSummarySection()}

      <S.Separator />

      <OrdersAccordion
        title={`${t("shipmentsId")} #${orderDetails?.orderReferenceNumber}`}
      >
        {orderDetails?.sales?.map((sale: OrderDetailSale, saleIndex) =>
          renderShipmentSection(sale, saleIndex),
        )}
      </OrdersAccordion>

      <S.Separator />

      <OrdersAccordion title={t("historyAndActivity")}>
        <S.ProductListsTitle
          style={{ marginTop: "1.5rem", marginBottom: "1.5rem" }}
        >
          {t("notes")}
        </S.ProductListsTitle>

        <S.NotesContainerAlign>
          <S.NotesContainer>
            <S.SumaryData style={{ marginBottom: 8 }}>
              {t("publicNotes")}
            </S.SumaryData>
            <S.ActivityTextArea
              style={{ maxHeight: "6rem" }}
              defaultValue={publicNotes}
              disabled
            />
          </S.NotesContainer>

          <S.NotesContainer>
            <S.SumaryData style={{ marginBottom: 8 }}>
              {t("privateNotes")}
            </S.SumaryData>
            <S.ActivityTextArea
              style={{ maxHeight: "6rem" }}
              defaultValue={privateNotes}
              disabled
            />
          </S.NotesContainer>
        </S.NotesContainerAlign>
      </OrdersAccordion>

      <S.Separator />

      {renderShippingAndBillingSection()}

      <S.Separator />

      {renderPriceSection()}
    </>
  );
}
